import { faSave, faExternalLink } from '@fortawesome/free-solid-svg-icons'
import { DateTime } from 'luxon'
import React, { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Content,
  Data,
  Navigation,
  Responsive,
  Layout,
  Search,
} from 'ui'
import { usePaginatedAdminSearch } from './usePaginatedAdminSearch'
import { CreateReportModal } from '../ReportsView/CreateReportModal'
import {
  useCreateReportMutation,
  ReportIndexType,
} from '../../../queries/useCreateReport'

const { Table, Cell, Row, Header } = Data

export const UserProfilesListView: React.FC = () => {
  const navigate = useNavigate()
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')

  const {
    currentPage,
    error,
    loading,
    profiles,
    handlePageClick,
    pageCount,
    queryString,
    tempQueryString,
    facets,
    toggleFacet,
    appliedFacets,
    tempFacets,
    hasUnappliedChanges,
    setTempSearchQuery,
    applyFilters,
    resetFilters,
    clearAllFilters,
    locationSearch,
  } = usePaginatedAdminSearch()

  // Manage modal...
  const [showModal, setShowModal] = useState(false)
  const dismissModal = () => setShowModal(false)

  // Handle report creation
  const canCreateReport =
    appliedFacets.length > 0 || (queryString?.length ?? 0) > 0
  const handleCreateReportClick = () => setShowModal(true)
  const [
    createReport,
    { loading: createReportLoading },
  ] = useCreateReportMutation()

  const handleCreateReport = async (name: string, description: string) => {
    try {
      await createReport({
        variables: {
          payload: {
            name,
            description: description || undefined,
            queryString: queryString || undefined,
            facetFilters: appliedFacets.length > 0 ? appliedFacets : undefined,
            indexType: ReportIndexType.Profiles,
          },
        },
      })
      setShowModal(false)
      // Navigate to reports page after successful creation
      navigate('/admin/reports')
    } catch (error) {
      console.error('Failed to create report:', error)
      // Keep modal open on error so user can retry
    }
  }

  return (
    <Layout.PaddedContent className="flex-grow w-full overflow-y-auto bg-white">
      <Helmet>
        <title>User Profiles - MCSS Pro Connect</title>
      </Helmet>
      <div
        className="flex-col flex w-full max-w-7xl mx-auto pb-32"
        style={{
          opacity: loading ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        <div className="flex-col flex sm:flex-row flex">
          <div className="w-full sm:w-1/2">
            <Content.Heading>User Profiles</Content.Heading>
          </div>
          <div className="ml-0 sm:ml-auto mt-3 sm:mt-auto w-full flex items-center">
            <div className="ml-auto flex">
              <Content.ActionButton
                icon={faSave}
                label="Create Report"
                disabled={!canCreateReport}
                onClick={handleCreateReportClick}
                className="ml-auto"
              />
            </div>
          </div>
        </div>
        <div className="flex-col flex sm:flex-row flex w-full">
          <div className="w-full sm:w-1/3 md:w-1/4 mr-0 sm:mr-2">
            <Search.FiltersCard
              onToggle={toggleFacet}
              onQuery={setTempSearchQuery}
              facets={facets ?? {}}
              appliedFacets={tempFacets}
              activeFacets={appliedFacets} // These are the actually applied facets that affect search
              initialQueryString={tempQueryString}
              onApplyFilters={() => applyFilters()}
              onResetFilters={() => clearAllFilters()}
              onEnterKeySubmit={() => applyFilters()}
              disableApplyButton={!hasUnappliedChanges}
              applyButtonDisabledMessage={
                !hasUnappliedChanges ? 'No changes to apply' : undefined
              }
              loading={loading}
            />
          </div>
          <div className="w-full sm:w-2/3 md:w-3/4 relative min-h-full">
            {loading && (
              <div className="mr-4 text-sm flex items-center text-gray-primary bg-white rounded-full shadow-sm p-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Searching...
              </div>
            )}
            <Table>
              <Row>
                <Header
                  width="40%"
                  className="!text-secondary !bg-transparent font-bold"
                >
                  Name
                </Header>
                <Header
                  width="30%"
                  className="!text-secondary !bg-transparent font-bold"
                >
                  Company / School
                </Header>
                <Header
                  width="20%"
                  className="!text-actionable !bg-transparent font-bold"
                >
                  Signup Date
                </Header>
                <Header
                  width="10%"
                  className="!text-secondary !bg-transparent"
                >
                  &nbsp;
                </Header>
              </Row>
              {error ? (
                <Row>
                  <Cell colSpan={4} className="text-red-500">
                    {error.message ||
                      'There was a problem fetching user profiles.'}
                  </Cell>
                </Row>
              ) : profiles && profiles.length > 0 ? (
                profiles.map((p) => (
                  <Row key={p?.id}>
                    <Cell width="40%">
                      <div className="flex">
                        <Avatar.Simple
                          name={p?.name || ''}
                          maxInitials={2}
                          color={p?.color ?? undefined}
                          size={36}
                          imageUrl={p?.avatarUrl ?? undefined}
                        />
                        <div className="my-auto pl-2 flex-col flex">
                          <span>{p?.name || ''}</span>
                          <span style={{ fontSize: '0.75rem' }}>
                            {p?.jobField || p?.title || ''}
                          </span>
                        </div>
                      </div>
                    </Cell>
                    <Cell width="30%">{p?.companyName || ''}</Cell>
                    <Cell width="20%">
                      {DateTime.fromISO(p?.createdAt || '').toFormat(
                        'MM/dd/yyyy'
                      )}
                    </Cell>
                    <Cell width="10%">
                      <Navigation.IconLink
                        icon={faExternalLink}
                        to={`/admin/profiles/show/${p?.id}${locationSearch}`}
                      />
                    </Cell>
                  </Row>
                ))
              ) : (
                <Row>
                  <Cell colSpan={4}>No results found.</Cell>
                </Row>
              )}
            </Table>
            {/* Pagination component */}
            <div
              style={{
                opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
              className="mt-6"
            >
              <Navigation.SimplePaginationBar
                onPageClick={handlePageClick({ historyPush: true })}
                currentPage={currentPage ?? 0}
                pageCount={pageCount ?? 0}
              />
            </div>
          </div>
        </div>
      </div>

      {showModal ? (
        <CreateReportModal
          confirmLabel="Create Report"
          loading={createReportLoading}
          onConfirm={handleCreateReport}
          onDismiss={dismissModal}
          title="Save this Report"
          placeholder="Enter a name for this"
        />
      ) : null}
    </Layout.PaddedContent>
  )
}
